import { Component, Input, Output, EventEmitter, OnInit, OnDestroy, OnChanges, SimpleChanges, ChangeDetectorRef } from '@angular/core';
import { Subscription } from 'rxjs';

import * as Objects from '@core/objects';
import { FilesListItemMenuClick } from '@core/components/files/lists/files-list-item-menu-click';
import { ThreeDotsMenuItem } from '@core/components/helpers/navigation/three-dots-menu/three-dots-menu-item';
import { VersionsViewService } from '@app/services/app/versions.service';
import { UrlService } from '@core/services/url/url.service';
import { AppService } from '@app/services/app/app.service';

import { VersionFeedback, FeedbackStatusCodeEnum } from '@core/models/version-feedback';
import * as flContexts from '@core/constants/files-list-contexts.constants';
import { InterfaceListenerService } from '@core/services/interface-listener/interface-listener.service';

@Component({
    selector: 'version-details-files-notes',
    templateUrl: './files-notes.component.html',
    styleUrls: ['./files-notes.component.scss']
})
export class VersionsDetailsContentFilesNotesComponent implements OnInit, OnDestroy, OnChanges {

    protected readonly PLUGIN_FEEDBACK = 'share_feedback';
    protected readonly VERSION_SHARE_FEEDBACK_CONTEXT = 'version-share-feedback';
    protected readonly SIGNATURE_OPTION_TYPE_CFR11 = 'cfr11';

    public readonly LOADING = 0;
    public readonly DONE = 1;
    public readonly ERROR = 2;

    public readonly PREVIEW_LOADING = 0;
    public readonly PREVIEW_DONE = 1;

    @Input() menu: Array<ThreeDotsMenuItem> = [];
    @Input() version: Objects.Version = null;
    //  files shared in this share with this user: to check if this file was shared with me. Could be also from other versions all shared together
    @Input() sharedWithMe: Array<number>; 
    @Input() share: any; // the user share
    @Input() feedbacks: Array<VersionFeedback> = null;
    @Output() action = new EventEmitter<FilesListItemMenuClick>();
    @Output() actionClick = new EventEmitter<Objects.File>();

    public files: Array<Objects.File> = null;
    public sharedFileIds: Array<number> = null;
    public notesCounter: Array<Objects.AnnotationsCounter> = [];
    public selectedFile: Objects.File = null;
    public status:number = this.LOADING;
    public previewStatus:number = this.PREVIEW_LOADING;
    public notesUrlPrefix: string = '';
    public annotationsUrlPrefix: string = '';
    public annotationsSaveUrlPrefix: string = '';
    public filesContext: string = flContexts.CTX.APP_ARTWORK_DETAILS;
    public signatureOptionType: string;
    public previewContext: string = flContexts.CTX.DEFAULT;
    public canCreateNotes: boolean = false;

    protected filesSubscriptor: Subscription;
    protected filesNotesSubscriptor: Subscription;
    protected appSubscriptor: Subscription;
    protected iListenerSubs: Subscription;
    protected user: Objects.User = null;
    protected userFeedback: VersionFeedback = null;

    private _accessFeedbacks: boolean | null = null;

    constructor(
        private urlSrv: UrlService,
        private cdr: ChangeDetectorRef,
        private viewVersionsSrv: VersionsViewService,
        private appSrv: AppService,
        private iListener: InterfaceListenerService ) { }

    // public getters 
    get accessFeedbacks(): boolean {
        return this._accessFeedbacks || false; // Return false if _accessFeedbacks is null
    }

    ngOnInit() {
        this.filesSubscriptor = this.viewVersionsSrv.selectFiles().subscribe((files: Objects.File[] ) => {
            this.files = files;
            if(files != null){
                this.loadSharedFilesNotesCounter();
            }
        });

        this.filesNotesSubscriptor = this.viewVersionsSrv.selectFeedbackFileNotesCounter().subscribe((counter: Objects.AnnotationsCounter[] ) => {
            // TODO may-2023: versions files notes should be counter
            this.notesCounter = counter;
            // console.log(" FILEs Notes counter get ", counter)
            this.init();
        });

        // TODO: get version id form subscriptor and set url prefixes...
        this.appSubscriptor = this.appSrv.getSession().subscribe( (session: any) => {
            this.user = session.current.user;
            this.signatureOptionType = session?.options?.organization.version_approvals_signature_type;
            this.updateFilesContext();

            this._accessFeedbacks = session?.options?.plugins?.some((pa) => pa == this.PLUGIN_FEEDBACK);
        //     if(this.accessFeedbacks){
        //         this.viewVersionSrv.loadShareFeedback(this.user);
        //         this.initFeedbacks();
        //     }else
        //         this.initView();
        });

        this.iListenerSubs = this.iListener.getVersionFeedbackDone().subscribe((version: Objects.Version) => {
            // Handle the version feedback 'onDone' event here to reload the notes counter.
            // hide the selected file preview if the feedback is done
            if(this.version != null && version!=null && version.id == this.version.id){
                // put the file preview in pdfjs to loading as we wait for Ws to arrive with new feedback status
                this.previewStatus = this.PREVIEW_LOADING;
                this.loadSharedFilesNotesCounter();
            }
        });

        if(this.feedbacks)
            this.loadMyFeedback();
    }

    ngOnDestroy() {
        this.filesSubscriptor.unsubscribe();
        this.filesNotesSubscriptor.unsubscribe();
        this.appSubscriptor.unsubscribe();
        this.iListenerSubs.unsubscribe();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if(changes.feedbacks){
            this.loadMyFeedback();
            if(this.selectedFile != null){
                // update conditions for the  opened file in preview to reload accces when open it again jan-2025 <diego@twonas.com>
                this.setAnnotationsControl(this.selectedFile);
            }
            if(this.feedbacks?.length > 0){
                this.updateFilesContext();
            }
        }
        if(changes.share && !changes.share.firstChange){
            if(this.selectedFile)
                this.setAnnotationsControl(this.selectedFile);
        }
    }
    

    init() : void {
        if(this.share != null){
            this.annotationsSaveUrlPrefix = "/web/share/feedback/"+this.share.entry.hash+"/versions/"+this.version.id;
        }
        if(this.files != null && this.notesCounter != null){
            // set urls for notes management.
            this.notesUrlPrefix = "/p/feedback/versions/"+this.version.id;
            this.annotationsUrlPrefix = "/versions/"+this.version.id+"/feedbacks";
            
            this.status = this.DONE;
        }
    }

    onMenuClick(menu: FilesListItemMenuClick) {
        console.debug(" clic on item ",menu.key);
        this.action.emit(menu);
        
    }

    // This now opens the preview of the file inline as messages
    actionFileClick(file: Objects.File){
        if(this.selectedFile != null && this.selectedFile.id == file.id){
            this.selectedFile = null;
        } else {
            this.setAnnotationsControl(file);
            this.selectedFile = file;
        }
    }

    onActionDownloadClick(file: Objects.File){
        window.location.href = this.urlSrv.build('/files/'+file.id);
    }

    setAnnotationsControl(file: Objects.File): void {

        this.previewStatus = this.PREVIEW_LOADING;
        this.cdr.detectChanges(); // to force the view to update when the file previewStatus is updated
        // TODO set save url in the case we have access
        this.canCreateNotes = (this.share?.options.get_comments && 
                                this.sharedWithMe.includes(file.id) && 
                                this.share.entry.user.id == this.user.id &&
                                this.userFeedback != null && (
                                        this.userFeedback.status?.status_code == FeedbackStatusCodeEnum.OPENED_CODE)


            );

        
        if( this.canCreateNotes){
            this.annotationsSaveUrlPrefix = "/web/share/feedback/"+this.share.entry.hash+"/versions/"+this.version.id;
            this.previewContext = this.VERSION_SHARE_FEEDBACK_CONTEXT;
        } else {
            this.annotationsSaveUrlPrefix = "";
            this.previewContext = flContexts.CTX.DEFAULT;
        }
        this.previewStatus = this.PREVIEW_DONE;
    }

    protected loadSharedFilesNotesCounter(): void {
        if(this.files != null && this.files.length > 0 ){
            // NO need to filter files to load notes only for the shared ones
            this.viewVersionsSrv.loadFeedbackFilesNotesCounter();
            this.init();
        } 
    }

    protected loadMyFeedback(){
        if (!this.accessFeedbacks || this.user == null){
            return;
        }

        if (this.version != null && this.feedbacks !=null){
            this.userFeedback = null;
            let myFBs = this.feedbacks.filter((f: VersionFeedback) => f.user.id == this.user.id );
            this.userFeedback = myFBs.length > 0 ? myFBs[0] : null;
        }
    }

    onSavedAnnotations(e:any) {
        this.loadSharedFilesNotesCounter();
    }

    protected updateFilesContext() : void {
        if( this.feedbacks?.length && this.signatureOptionType === this.SIGNATURE_OPTION_TYPE_CFR11){
            this.filesContext = flContexts.CTX.APP_ARTWORK_DETAILS_FEEDBACK;
        } else {
            this.filesContext = flContexts.CTX.APP_ARTWORK_DETAILS;
        }
    }


}
